import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxArchive,
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleXmark,
  faEllipsisV,
  faEnvelope,
  faTrash,
  faLock,
  faLockOpen,
  faPenToSquare,
  faRotate,
  faSpinner,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import { chaseTableActions, enhancedloanStatusData } from "./chaseListData";
import React, { Fragment, useState } from "react";
import { useChaseMenu } from "../../store/use-chase-menu";
import ChaseDropMenu from "./ChaseDropMenu";
import { Menu, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import DeleteModel from "../../models/delete-model";
import { useDeleteModel } from "../../store/use-delete-model";
import EditChaseModel from "../../models/chase-edit-model";
import { useChaseEditModel } from "../../store/use-chase-edit-model";
import moment from "moment";
import GetUser from "../../utils/getUser";
import NotesModal from "../../models/notes-model";
import { useSuretyModel } from "../../store/use-surety-popup";
import PopupSuretyModel from "../../models/popup-surety";
import IsChaseOverSix from "../../utils/is-chase-over-six";
import fetchChaseDetail from "../../utils/fetchChaseDetail";
import EditV2PlanModel from "../../models/edit-v2-plan-model";
import { useEditV2PlanModel } from "../../store/use-edit-v2-plan-model";

interface TChaseRenderitem {
  item: any;
  index: number;
}

const getTemporaryStatus = ({
  log,
  status,
  item,
}: {
  log?: { attempt: number; paymentPlan: string };
  status: string;
  item: Record<string, any>;
}) => {
  if (status === "Awaiting-Customer" && item?.partialPaymentAmount > 0) {
    return "Partial";
  } else if (
    log?.attempt < Number(log?.paymentPlan?.split("-")[0]) &&
    status === "On-Hold"
  ) {
    return "Plan";
  } else {
    return status;
  }
};

const getStatus = (status: string) => {
  const statusData = enhancedloanStatusData.find(
    (stat) => stat.name === (status || "default")
  );
  return statusData;
};

const ChaseRenderitem: React.FC<TChaseRenderitem> = ({
  item: itemData,
  index,
}) => {
  const user = GetUser();
  const [item, setItem] = useState<Record<string, any> | null>(itemData);
  const [allowRobot, setAllowRobot] = useState<boolean | null>(
    item?.allowRobot
  );
  const [isMessagePopup, setIsMessagePopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isExpanding, setIsExpanding] = useState<boolean>(false);
  const { initialValues, onToggleChaseMenu, onChaseMenuOpen } = useChaseMenu();
  const {
    initialValues: deletePopupInitialValues,
    onOpenDeletePopup,
    onCloseDeletePopup,
  } = useDeleteModel();
  const {
    onOpenSuretyPopup,
    onCloseSuretyPopup,
    initialValues: suretyInitialValues,
  } = useSuretyModel();
  const { initialValues: editInitialValues, onOpen: onEditOpen } =
    useChaseEditModel();
  const { initialValues: editV2PlanInitialValues, onOpen: onEditV2PlanOpen } =
    useEditV2PlanModel();
  // v2 plan actions are for admins and super admins only, the server checks this too
  const canManageV2Plan =
    ["super-admin", "admin"].includes(user?.type) && item?.hasActiveV2Plan;
  let isTemporaryStatus = getTemporaryStatus({
    log: item?.log,
    status: item?.status,
    item: itemData,
  });
  const lastDate = new Date(item?.startDate);
  lastDate.setDate(lastDate.getDate() + 30);
  const isExpanded = initialValues?.id === item?.id;

  // --------------------------- [ DELETE CHASE ] ------------------------------
  const HandleDeleteChase = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.delete(`/loan/${item?.id}`);
      onCloseDeletePopup();
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // --------------------------- [ DELETE CHASE ] ------------------------------
  const HandletoggleArchive = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/loan/toggle/archive/${item?.id}`);
      onCloseSuretyPopup();
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // --------------------------- [ HANDLE ON EDIT CALL ] ------------------------------

  const HandleEditChase = () => {
    onEditOpen({ id: item?.id, data: item });
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const UpdateItemData = async () => {
    try {
      const resData = await fetchChaseDetail(item?.id);

      if (!resData) {
        return;
      }

      onChaseMenuOpen({
        id: resData?.id,
        data: resData,
        timeline: resData?.timeline,
        logs: resData?.logs,
      });
      setItem(resData);
    } catch (error) {
      console.log(error);
    }
  };

  // ------------------------------- [ EXPAND / COLLAPSE THE CHASE ] ------------------------------
  // The list is loaded lean while browsing, so the timeline and the logs are fetched
  // the first time this row is opened and then kept on the row for later expands.

  const ToggleChaseDetail = async () => {
    if (isExpanded || item?.timeline) {
      onToggleChaseMenu({
        id: item?.id,
        data: item,
        timeline: item?.timeline,
        logs: item?.logs,
      });
      return;
    }

    setIsExpanding(true);
    try {
      const resData = await fetchChaseDetail(item?.id);

      if (resData) {
        setItem(resData);
        onChaseMenuOpen({
          id: resData?.id,
          data: resData,
          timeline: resData?.timeline,
          logs: resData?.logs,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
    setIsExpanding(false);
  };

  // ------------------------------- [ TOGGLE ROBOT ] ------------------------------
  const ToggleRobot = async () => {
    try {
      const res = await axiosInstance.patch(`/loan/toggle/robot/${item?.id}`);
      setAllowRobot((prev) => !prev);
      toast.success(res.data?.message);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
  };

  // ------------------------------- [ CANCEL PAYMENT PLAN ] ------------------------------
  const CancelPaymentPlan = async () => {
    try {
      const res = await axiosInstance.post(`/loan/cancel/plan/${item?.id}`);
      toast.success(res.data?.message);
      UpdateItemData();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
  };

  // ------------------------------- [ CANCEL V2 PAYMENT PLAN ] ------------------------------
  const CancelV2PaymentPlan = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/loan/cancel/v2-plan/${item?.id}`);
      onCloseSuretyPopup();
      setLoading(false);
      toast.success(res.data?.message);
      UpdateItemData();
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  // ------------------------------- [ CANCEL PAYMENT PLAN ] ------------------------------
  const RemoveChaseFromICB = async () => {
    try {
      const res = await axiosInstance.post(`/loan/cancel/icb/${item?.id}`);
      toast.success(res.data?.message);
      UpdateItemData();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const ToggleMessagesPopup = () => {
    setIsMessagePopup(!isMessagePopup);
  };

  if (!item) {
    return null;
  }

  return (
    <>
      <tr
        className={clsx(
          "relative font-medium border-b border-edge-faint transition-colors duration-200 hover:bg-overlay-hover",
          isExpanded && "bg-overlay"
        )}
        key={index}
      >
        <td className="w-[19%] py-3.5 pl-1 align-top">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="text-[14px] font-semibold text-text-primary">
              {item?.firstName + " " + item.surname}
            </p>
            {item?.createdFrom ? (
              <span
                className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-light-gray border border-edge rounded-full px-2 py-[3px]"
                title={
                  item?.createdFrom === "A"
                    ? "Created automatically"
                    : "Created manually"
                }
              >
                {item?.createdFrom === "A" ? "Auto" : "Manual"}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.policyReferenceNumber}
          </p>
          {item?.hasOpenSyncFailure ? (
            <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-[0.08em] text-rose-600 dark:text-rose-300 border border-rose-500/40 bg-rose-500/10 rounded-full px-2 py-[3px]">
              CDL sync failed
            </span>
          ) : null}
        </td>

        <td className="py-3.5 w-[21%] align-top">
          <p className="text-[13px] text-text-primary break-all">
            {item?.email}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray tabular-nums">
            {item?.phone}
          </p>
        </td>

        <td className="py-3.5 w-[14%] align-top">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {moment(item?.startDate).format("DD MMM YYYY")}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray whitespace-nowrap">
            Ends {moment(lastDate).format("DD MMM YYYY")}
          </p>
        </td>

        <td className="py-3.5 w-[10%] align-middle text-right">
          <p className="text-[14px] font-bold text-text-primary tabular-nums whitespace-nowrap">
            £{item.partialPaymentAmount?.toFixed(2) || "0.00"}
          </p>
        </td>

        <td className="py-3.5 w-[10%] align-middle text-right">
          <p className="text-[14px] font-bold text-text-primary tabular-nums whitespace-nowrap">
            £{item.outstandingBalance?.toFixed(2)}
          </p>
        </td>
        {/* Activity — restart count, six-month flag, and whether anything is collected.
            Each icon is paired with a title so the meaning isn't carried by shape alone. */}
        <td className="py-3.5 w-[9%] align-middle">
          <div className="flex items-center justify-center gap-2.5">
            <span
              className="flex items-center gap-1 text-light-gray"
              title={`Restarted ${item?.restarted || 0} time(s)`}
            >
              <FontAwesomeIcon icon={faRotate} className="text-[11px]" />
              <span className="text-[12px] font-semibold tabular-nums">
                {item?.restarted || 0}
              </span>
            </span>

            {IsChaseOverSix(item) ? (
              <span
                className="px-1.5 py-[2px] rounded-full text-[9px] font-bold bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300"
                title="Chase is over six months old"
              >
                6M
              </span>
            ) : null}

            <FontAwesomeIcon
              icon={item?.partialPaymentAmount ? faCircleCheck : faCircleXmark}
              className={clsx(
                "text-[13px]",
                item?.partialPaymentAmount
                  ? "text-emerald-600 dark:text-emerald-300"
                  : "text-light-gray/60"
              )}
              title={
                item?.partialPaymentAmount
                  ? "Part payment received"
                  : "Nothing collected yet"
              }
            />
          </div>
        </td>
        <td className="py-3.5 w-[12%] align-middle">
          {(() => {
            const status = getStatus(isTemporaryStatus || item?.status);
            if (!status) return null;
            return (
              <div
                className={`inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full ${status.bgColor}`}
              >
                <FontAwesomeIcon
                  icon={status.icon}
                  className={clsx("text-[11px]", status.color)}
                />
                <p
                  className={`text-[11px] font-semibold whitespace-nowrap ${status.color}`}
                >
                  {status.name?.split("-")[0].length > 4
                    ? status.name?.split("-")[0]
                    : status.name?.replace("-", " ")}
                </p>
              </div>
            );
          })()}
        </td>

        {/* Actions grouped into a single cell so the row ends on one aligned control
            cluster rather than three separate columns of loose icons. */}
        <td className="py-3.5 align-middle">
          <div className="flex items-center justify-end gap-0.5">
            {/* Opens the notes/messages modal — an envelope, not a bell. A bell reads
                as notifications and misnames what this button actually does. */}
            <button
              type="button"
              onClick={ToggleMessagesPopup}
              title="Notes & messages"
              className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-[15px]" />
            </button>

            <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button
                data-bs-toggle="dropdown"
                title="More actions"
                className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
              >
                <FontAwesomeIcon
                  icon={faEllipsisV}
                  className="text-[15px]"
                />
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="transform opacity-0 scale-95 -translate-y-1"
              enterTo="transform opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-100"
              leaveFrom="transform opacity-100 scale-100 translate-y-0"
              leaveTo="transform opacity-0 scale-95 -translate-y-1"
            >
              <div className="dropdown-menu p-[0] bg-transparent border-none absolute top-[20px] right-0 z-[99999] origin-top-right">
                <div
                  className={clsx(
                    "z-10 mt-2 py-1 text-light-gray w-[auto] min-w-[150px] rounded-xl bg-surface-elevated border border-edge shadow-overlay"
                  )}
                >
                  <div>
                    {!item?.archived ? (
                      <>
                        {chaseTableActions.map((action, index) => {
                          return (
                            <div
                              key={index}
                              role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                              onClick={() => {
                                action.name === "Edit Chase"
                                  ? HandleEditChase()
                                  : onOpenDeletePopup({
                                      id: item.id,
                                      onClick: HandleDeleteChase,
                                    });
                              }}
                            >
                              <FontAwesomeIcon
                                icon={action?.icon}
                                className="text-xs"
                              />
                              {action.name}
                            </div>
                          );
                        })}

                        {user?.type === "agent" ? null : (
                          <div
                            role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                            onClick={ToggleRobot}
                          >
                            <FontAwesomeIcon
                              icon={allowRobot ? faLock : faLockOpen}
                              className="text-xs"
                            />
                            {allowRobot ? "Block" : "Unblock"} Robot
                          </div>
                        )}

                        {canManageV2Plan ? (
                          <>
                            <div
                              role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                              onClick={() =>
                                onEditV2PlanOpen({ id: item?.id, data: item })
                              }
                            >
                              <FontAwesomeIcon
                                icon={faPenToSquare}
                                className="text-xs"
                              />
                              Edit Plan (V2)
                            </div>
                            <div
                              role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                              onClick={() =>
                                onOpenSuretyPopup({
                                  id: item?.id,
                                  onClick: CancelV2PaymentPlan,
                                  title:
                                    "Are you sure you want to cancel this v2 payment plan?",
                                  description:
                                    "The plan is cancelled with CDL, the remaining instalments are no longer charged and the outstanding balance becomes due. This cannot be undone.",
                                  confirmLabel: "Cancel plan",
                                  tone: "danger",
                                })
                              }
                            >
                              <FontAwesomeIcon
                                icon={faXmarkCircle}
                                className="text-xs"
                              />
                              Cancel Plan (V2)
                            </div>
                          </>
                        ) : null}

                        {!item?.hasActiveV2Plan &&
                        getStatus(isTemporaryStatus || item?.status).name ===
                          "Plan" ? (
                          <div
                            role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                            onClick={() =>
                              onOpenSuretyPopup({
                                id: item?.id,
                                onClick: CancelPaymentPlan,
                                title:
                                  "Are you sure you want to cancel this payment plan?",
                                description:
                                  "The remaining instalments stop being collected and the full outstanding balance becomes due. This cannot be undone.",
                                confirmLabel: "Cancel plan",
                                tone: "danger",
                              })
                            }
                          >
                            <FontAwesomeIcon
                              icon={faXmarkCircle}
                              className="text-xs"
                            />
                            Cancel Plan
                          </div>
                        ) : null}

                        {user?.type !== "agent" &&
                        getStatus(isTemporaryStatus || item?.status).name ===
                          "Debt-Collectors" ? (
                          <div
                            role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                            onClick={() =>
                              onOpenSuretyPopup({
                                id: item?.id,
                                onClick: RemoveChaseFromICB,
                                title:
                                  "Remove this chase from the debt collectors?",
                                description:
                                  "The chase is recalled from ICB and returns to your queue. You will need to hand it over again to resume collection.",
                                confirmLabel: "Remove from ICB",
                                tone: "danger",
                              })
                            }
                          >
                            <FontAwesomeIcon
                              icon={faXmarkCircle}
                              className="text-xs"
                            />
                            Remove From ICB
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    <div
                      role="menuitem"
                              className="flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150"
                      onClick={() => {
                        onOpenSuretyPopup({
                          id: item?.id,
                          onClick: HandletoggleArchive,
                          title: item?.archived
                            ? "Are you sure you want to unarchive this chase?"
                            : "Are you sure you want to archive this chase?",
                          description: item?.archived
                            ? "It returns to the active chase list and starts sending again."
                            : "It moves to Customer Archives, stops sending, and its history is parked. You can unarchive it later.",
                          confirmLabel: item?.archived
                            ? "Unarchive"
                            : "Archive",
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faBoxArchive} className="text-xs" />
                      {item?.archived ? "Unarchive" : "Archive"} Chase
                    </div>

                    {item?.archived ? (
                      <div
                        role="menuitem"
                        className="flex gap-2 px-4 py-2 text-xs text-red-600 dark:text-red-300 items-center font-medium cursor-pointer hover:bg-red-50 dark:hover:bg-red-500/15 transition-colors duration-150"
                        onClick={() =>
                          onOpenDeletePopup({
                            id: item.id,
                            onClick: HandleDeleteChase,
                          })
                        }
                      >
                        {/* Destructive and irreversible — coloured so it can't be hit by muscle memory. */}
                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        Delete Chase
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Transition>
          </Menu>

            <button
              type="button"
              onClick={ToggleChaseDetail}
              disabled={isExpanding}
              title={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              aria-busy={isExpanding}
              className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200 disabled:cursor-wait"
            >
              {/* Compared against item.id — this previously read item.taskId, which is
                  never the menu id, so the chevron never reflected the open state. */}
              <FontAwesomeIcon
                icon={
                  isExpanding
                    ? faSpinner
                    : isExpanded
                    ? faChevronUp
                    : faChevronDown
                }
                spin={isExpanding}
                className="text-[13px]"
              />
            </button>
          </div>
        </td>
      </tr>
      <tr className="relative z-10">
        {initialValues?.id === item?.id && (
          <ChaseDropMenu UpdateItemData={UpdateItemData} />
        )}
      </tr>
      <tr>
        <td>
          {deletePopupInitialValues?.id === item?.id && (
            <DeleteModel loading={loading} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {suretyInitialValues?.id === item?.id && (
            <PopupSuretyModel loading={loading} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {editInitialValues.id === item?.id && (
            <EditChaseModel UpdateItemData={UpdateItemData} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {editV2PlanInitialValues.id === item?.id && <EditV2PlanModel />}
        </td>
      </tr>

      {isMessagePopup ? (
        <NotesModal
          onToggle={ToggleMessagesPopup}
          data={{
            policyNumber: item?.policyReferenceNumber,
            customerName: [item?.firstName, item?.surname]
              .filter(Boolean)
              .join(" "),
          }}
        />
      ) : null}
    </>
  );
};

export default ChaseRenderitem;
